'use client';

import React from 'react';
import styled from 'styled-components';
import { URLParameters } from '@/app/common/urlParameters.ts';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider.tsx';
import { DebugAuth } from '@/app/components/common/DebugAuth.tsx';
import { DebugUserIdLine } from '@/app/components/common/DebugUserId.tsx';
import { DebugValuesLines } from '@/app/components/common/DebugValues.tsx';
import { DebugContainer, DebugInfo } from '@/app/components/search/components/Banners/Debug/styled.ts';
import { getSearchParamsClient } from '@/app/components/search/common/getSearchParams.ts';
import { DEBUG_VISIBILITY, useDebugVisibility } from '@/app/components/hotel-detail/hooks/useDebugVisibility.ts';

/** An absent id is the thing worth seeing here, so every line renders even when it has no value. */
const NO_VALUE = '—';

const DebugInfoList = styled.div`
  width: 100%;
`;

/**
 * `DebugContainer` is `width: 100%` because on the results page it sits in a grid cell with no
 * gutter of its own. Here both detail layouts inset it with a horizontal margin, and a 100% width
 * on top of that pushes the right border past the viewport — so the width goes back to auto and
 * the grid item stretches into whatever the margins leave.
 */
const Panel = styled(DebugContainer)`
  width: auto;
`;

/**
 * The detail-page counterpart of the results-page `DebugBanner`, reusing its container and line
 * styles so the two pages read as the same panel.
 *
 * It carries the ids needed to follow a single visit across the funnel: the Vacation Catalog search
 * id, the searchId, the resolved business profile and — only when `debug_values` lists `userId` —
 * the user id. Every line renders even with no value: a detail page reached with a re-minted user
 * id or a dropped vcSearchId is exactly the case being debugged, and a hidden line is
 * indistinguishable from a panel that never rendered.
 *
 * `businessProfile` is the profile the page actually resolved (from `localization`), which is not
 * necessarily the `businessProfileId` carried in the URL.
 */
export const DebugPanel = (props: React.HTMLAttributes<HTMLDivElement>) => {
  const { localization, searchInfoData, searchParams: searchParamsServer } = usePageWrapperData();
  const visibility = useDebugVisibility();

  // Debug has to be switched on for the page before any of this renders — `debug=true` in the URL,
  // or a telesales session. The `debug_values` toggle decides what the panel carries, never
  // whether it opens, so turning it on for a business profile shows nothing to ordinary
  // visitors. (MPH-1086)
  if (visibility === DEBUG_VISIBILITY.HIDDEN) return null;

  // Server params as the fallback, like the results banner: reading window.location on its own
  // renders the placeholder on the server and the id on the client, i.e. a hydration mismatch.
  const vcSearchId = getSearchParamsClient(searchParamsServer)[URLParameters.VC_SEARCH_ID];

  return (
    <Panel data-testid="debug-panel" {...props}>
      {visibility === DEBUG_VISIBILITY.VISIBLE ? (
        <DebugInfoList>
          {/* Same opt-in for an authorised viewer as for an anonymous one: the id appears only
              when `debug_values` lists `userId`, so there is one rule rather than one per branch. */}
          <DebugUserIdLine />
          <DebugInfo>
            VC Search Id: <strong>{vcSearchId || NO_VALUE}</strong>
          </DebugInfo>
          <DebugInfo>
            Search Id: <strong>{searchInfoData?.searchId || NO_VALUE}</strong>
          </DebugInfo>
          <DebugInfo>
            Business profile: <strong>{localization?.businessProfile || NO_VALUE}</strong>
          </DebugInfo>
        </DebugInfoList>
      ) : (
        <>
          <DebugUserIdLine />
          <DebugAuth />
        </>
      )}
      <DebugValuesLines />
    </Panel>
  );
};

export default DebugPanel;
